import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom'
import { themeContext } from 'contexts/theme_context/ThemeContext'
import { languageContext } from 'contexts/language_context/LanguageContext'
import { VehicleContext } from 'contexts/vehicles_context/VehiclesContext'
import { AuthContext } from 'contexts/auth_context/AuthContext'
import apiClient, {
    AUTH_EXPIRED_EVENT,
    buildAuthHeaders,
    isRequestCanceled,
} from 'services/apiClient'
import './Tables.css';
import LoadingScreen from 'components/loading_screen/LoadingScreen'
import VehicleSearchForm from './VehicleSearchForm';
import { fetchDailyVehicleData } from 'services/vehicleService';
import LicensePlate from 'components/license_plate/LicensePlate';
import VehicleTableRow from './VehicleTableRow';
import {
    buildPlateLookupCandidates,
    filterVehiclesByPlateSearch,
    hasPlateSearchInput,
    validatePlateSearch,
} from '../../utils/licensePlateSearch';

function useVehicleForm(initialPlateLetters = ['', '', ''], initialPlateNumber = '') {
    const [plateLetters, setPlateLetters] = useState(initialPlateLetters);
    const [plateNumber, setPlateNumber] = useState(initialPlateNumber);
    const [langErr, setLangErr] = useState('d-none');
    const [numberErr, setNumErr] = useState('d-none');

    return { plateLetters, setPlateLetters, plateNumber, setPlateNumber, langErr, setLangErr, numberErr, setNumErr };
}

const noop = () => {};
const ENRICHMENT_CONCURRENCY = 6;

const createCanceledError = () => {
    const error = new Error('Request canceled.');
    error.name = 'AbortError';
    error.code = 'ERR_CANCELED';
    return error;
};

const throwIfAborted = (signal) => {
    if (signal?.aborted) {
        throw createCanceledError();
    }
};

const enrichWithConcurrency = async (vehicles, concurrency, signal, worker) => {
    const results = new Array(vehicles.length);
    let nextIndex = 0;
    const workerCount = Math.min(concurrency, vehicles.length);

    await Promise.all(Array.from({ length: workerCount }, async () => {
        while (nextIndex < vehicles.length) {
            throwIfAborted(signal);
            const currentIndex = nextIndex;
            nextIndex += 1;
            results[currentIndex] = await worker(vehicles[currentIndex], currentIndex);
        }
    }));

    throwIfAborted(signal);
    return results;
};

const getVehicleDisplayName = (vehicle = {}) => {
    const makeModel = [vehicle.make, vehicle.model].filter(Boolean).join(' ').trim();
    return vehicle.vehicle_name || vehicle.car_name || makeModel || '--';
};

export const buildVehicleIdentityFilter = (vehicle) => {
    if (!vehicle) return null;
    const makeModel = [vehicle.make, vehicle.model].filter(Boolean).join(' ').trim();

    return {
        chassis_number: vehicle.chassis_number || '',
        license_plate: vehicle.license_plate || '',
        vehicle_name: vehicle.vehicle_name || '',
        car_name: vehicle.car_name || '',
        make: vehicle.make || '',
        model: vehicle.model || '',
        vehicle_label: vehicle.vehicle_name || vehicle.car_name || makeModel || '',
    };
};


export default function FleetOverview({
    type = 'vehicles',
    isEmbedded = false,
    showTitle = false,
    isActive = true,
    onVehicleFilterChange = noop,
}) {
    const { plateLetters, setPlateLetters, plateNumber, setPlateNumber } = useVehicleForm();
    const navto = useNavigate()
    const resolvedType = type || 'vehicles'
    const [isLoading, setIsLoading] = useState(false);
    const [isLangError, setIsLangError] = useState(false);
    const [langErrorMessage, setLangErrorMessage] = useState('');
    const [isNumError, setIsNumError] = useState(false);
    const [numErrorMessage, setNumErrorMessage] = useState('');
    const [data, setData] = useState()
    const [item, setItem] = useState()
    const { webtheme } = useContext(themeContext)
    const [fields, setFields] = useState()
    const { readCookie } = useContext(AuthContext)
    const [showMore, setShowMore] = useState(false)
    const [activeSearchedVehicle, setActiveSearchedVehicle] = useState(null)
    const [searchStatus, setSearchStatus] = useState('idle')
    const requestRunRef = useRef({ id: 0, controller: null });
    let { weblanguage, jsonfile } = useContext(languageContext)
    let { vehWithComp, refreshVehicles } = useContext(VehicleContext)
    const tableFields = fields || {};
    const isVehicleSearchLoading = searchStatus === 'searching';
    const sourceVehicles = useMemo(() => (Array.isArray(data) ? data : []), [data]);
    const hasLiveVehicleData = sourceVehicles.length > 0;
    const hasCurrentPlateSearchInput = hasPlateSearchInput({ plateNumber, plateLetters });
    const liveFilteredVehicles = useMemo(() => (
        filterVehiclesByPlateSearch(sourceVehicles, { plateNumber, plateLetters })
    ), [sourceVehicles, plateNumber, plateLetters]);
    const displayedVehicles = activeSearchedVehicle
        ? [activeSearchedVehicle]
        : hasCurrentPlateSearchInput
            ? liveFilteredVehicles
            : sourceVehicles;
    const hasActiveSearchState = Boolean(activeSearchedVehicle || searchStatus !== 'idle');
    const showNoVehiclesMessage = Array.isArray(data) && displayedVehicles.length === 0;
    const fleetOverviewCopy = jsonfile?.[weblanguage]?.fleetOverview || {};
    const liveLoadingTitle = fleetOverviewCopy.loadingTitle || 'Loading live fleet data';
    const liveLoadingMessage = fleetOverviewCopy.loadingMessage || 'Fetching the latest vehicle status and telemetry.';
    const liveRefreshingTitle = fleetOverviewCopy.refreshingTitle || 'Updating live fleet data';
    const liveRefreshingMessage = fleetOverviewCopy.refreshingMessage || 'Current vehicles remain available while fresh telemetry arrives.';

    const startRequestRun = useCallback(() => {
        requestRunRef.current.controller?.abort();
        const controller = new AbortController();
        const nextRun = {
            id: requestRunRef.current.id + 1,
            controller,
        };
        requestRunRef.current = nextRun;
        return nextRun;
    }, []);

    const abortCurrentRun = useCallback(() => {
        requestRunRef.current.controller?.abort();
    }, []);

    const abortRun = useCallback((run) => {
        if (requestRunRef.current.id === run.id) {
            run.controller.abort();
        }
    }, []);

    const isCurrentRun = useCallback((run) => (
        requestRunRef.current.id === run.id && !run.controller.signal.aborted
    ), []);

    const resetSearchFeedback = () => {
        setIsLangError(false);
        setLangErrorMessage('');
        setIsNumError(false);
        setNumErrorMessage('');
    };

    const clearVehicleSearchState = () => {
        onVehicleFilterChange(null);
        setActiveSearchedVehicle(null);
        setSearchStatus('idle');
        setShowMore(false);
    };

    const clearExactVehicleSelection = () => {
        onVehicleFilterChange(null);
        setActiveSearchedVehicle(null);
        setShowMore(false);
        if (searchStatus !== 'searching') {
            setSearchStatus('idle');
        }
    };

    const handlePlateNumberChange = (nextPlateNumber) => {
        resetSearchFeedback();
        clearExactVehicleSelection();
        setPlateNumber(nextPlateNumber);
    };

    const handlePlateLettersChange = (nextPlateLetters) => {
        resetSearchFeedback();
        clearExactVehicleSelection();
        setPlateLetters(nextPlateLetters);
    };

    const handleClearSearch = () => {
        setPlateNumber('');
        setPlateLetters(['', '', '']);
        resetSearchFeedback();
        clearVehicleSearchState();
    };


    const fetchAdditionalVehicleData = useCallback(async (chassisNumber, cookie, options = {}) => {
        const headers = {
            ...buildAuthHeaders(cookie)
        };
        const params = {
            chassis_number: chassisNumber
        };
        try {
            const response = await apiClient.get('shiftware/api/mongo-backup-documents/', {
                headers,
                params,
                ...(options.signal ? { signal: options.signal } : {}),
            });
            return response.data ? response.data : null;
        } catch (error) {
            if (isRequestCanceled(error) || options.signal?.aborted) {
                throw error;
            }
            return null;
        }
    }, []); // Remember to include dependencies if any

    const getSingleVehicle = async () => {
        const validation = validatePlateSearch({ plateNumber, plateLetters });
        setPlateNumber(validation.plateNumber);
        setPlateLetters(validation.plateLetters);

        if (!validation.isValidNumber || !validation.isValidLetters) {
            setIsNumError(!validation.isValidNumber);
            setNumErrorMessage(!validation.isValidNumber ? tableFields.numberErr || 'Please add 3-4 English numbers.' : '');
            setIsLangError(!validation.isValidLetters);
            setLangErrorMessage(!validation.isValidLetters ? tableFields.letterErr || 'Please add 2-3 Arabic letters.' : '');
            clearVehicleSearchState();
            return;
        }

        resetSearchFeedback();
        const run = startRequestRun();
        const signal = run.controller.signal;
        setShowMore(false);
        setSearchStatus('searching');
        setIsLoading(true);
        const cookie = readCookie();

        try {
            let vehicleData = null;
            const lookupCandidates = buildPlateLookupCandidates(validation);
            for (const lookupValue of lookupCandidates) {
                throwIfAborted(signal);
                vehicleData = await fetchVehicleData(lookupValue, cookie, run);
                if (vehicleData) break;
            }

            if (!isCurrentRun(run)) return;
            if (vehicleData) {
                const vehicleFilter = buildVehicleIdentityFilter(vehicleData);
                setActiveSearchedVehicle(vehicleData);
                onVehicleFilterChange(vehicleFilter);
                setSearchStatus('found');
                setShowMore(true);
            } else {
                const localMatchesForSubmittedPlate = filterVehiclesByPlateSearch(sourceVehicles, validation);
                onVehicleFilterChange(null);
                setActiveSearchedVehicle(null);
                setSearchStatus(localMatchesForSubmittedPlate.length > 0 ? 'no-exact-local' : 'not-found');
            }
        } catch (error) {
            if (isRequestCanceled(error) || signal.aborted) return;
            if (!isCurrentRun(run)) return;
            const localMatchesForSubmittedPlate = filterVehiclesByPlateSearch(sourceVehicles, validation);
            onVehicleFilterChange(null);
            setActiveSearchedVehicle(null);
            setSearchStatus(localMatchesForSubmittedPlate.length > 0 ? 'no-exact-local' : 'not-found');
        } finally {
            if (isCurrentRun(run)) {
                setIsLoading(false);
            }
        }
    };

    async function fetchVehicleData(plate, cookie, run) {
        const signal = run?.controller.signal;
        try {
            throwIfAborted(signal);
            // Fetch initial vehicle data
            const vehicleResponse = await apiClient.get(`shiftware/vehicles-license/${plate}`, {
                headers: {
                    'Content-Type': 'application/json',
                    ...buildAuthHeaders(cookie),
                },
                ...(signal ? { signal } : {}),
            });
            throwIfAborted(signal);
            let vehicleData = vehicleResponse.data ? vehicleResponse.data : null;
            if (!vehicleData) {
                return null;
            }

            let chassis = vehicleData.chassis_number;

            vehicleData['complaints'] = [];
            vehicleData['vehicle_name'] = vehicleResponse.data.vehicle_name;

            const [mongoData, dailyVehicleData] = await Promise.all([
                fetchAdditionalVehicleData(chassis, cookie, { signal }),
                fetchDailyVehicleData(chassis, cookie, { signal }),
            ]);
            throwIfAborted(signal);

            // Fetch and merge mongo vehicle data
            if (mongoData && mongoData.length > 0) {
                vehicleData['soc'] = mongoData[0].soc;
                vehicleData['mileage'] = mongoData[0].mileage_dm;
            } else {
                vehicleData['soc'] = null;
                vehicleData['mileage'] = null;
            }

            // Fetch daily vehicle data and merge
            if (dailyVehicleData) {
                // Transforming the array of objects into a dictionary with date as the key, including daily mileage
                let datedEffWithMileage = dailyVehicleData.reduce((dict, record) => {
                    dict[record.date] = { efficiency: record.efficiency, dailyMileage: record.dailyMileage };
                    return dict;
                }, {});
                vehicleData['dated_eff'] = datedEffWithMileage;
            } else {
                vehicleData['dated_eff'] = {}; // Default to an empty object if no data
            }
            return vehicleData;
        } catch (error) {
            if (isRequestCanceled(error) || signal?.aborted) {
                throw error;
            }
            return null;
        }
    }

    const getAllVehicles = async () => {
        const run = startRequestRun();
        const signal = run.controller.signal;
        setIsLoading(true); // Start loading
        setPlateNumber('');
        setPlateLetters(['', '', '']);
        resetSearchFeedback();
        clearVehicleSearchState();
        const cookie = readCookie();

        try {
            const vehiclesData = await enrichWithConcurrency(
                vehWithComp,
                ENRICHMENT_CONCURRENCY,
                signal,
                async (vehicle) => {
                    const liveData = await fetchAdditionalVehicleData(vehicle.chassis_number, cookie, { signal });
                    throwIfAborted(signal);
                    return liveData
                        ? { ...vehicle, soc: liveData[0].soc, mileage: liveData[0].mileage_dm, timestamp: liveData[0].timestamp }
                        : { ...vehicle, soc: null, mileage: null, timestamp: null };
                }
            );

            if (isCurrentRun(run)) {
                setData(vehiclesData);
            }
        } catch (error) {
            if (!isRequestCanceled(error) && !signal.aborted) {
                // Keep the currently displayed vehicles on enrichment failure.
            }
        } finally {
            if (isCurrentRun(run)) {
                setIsLoading(false); // End loading after all data is fetched
            }
        }
    };

    useEffect(() => {
        if (!isActive) return;

        if (vehWithComp.length === 0) {
            const user = readCookie()
            if (user) {
                const run = startRequestRun();
                const signal = run.controller.signal;
                setIsLoading(true);
                refreshVehicles(user, { signal })
                    .catch((error) => {
                        if (!isRequestCanceled(error) && !signal.aborted) {
                            // Keep the existing empty/loading state when the base vehicle fetch fails.
                        }
                    })
                    .finally(() => {
                        if (isCurrentRun(run)) setIsLoading(false);
                    });
                return () => {
                    abortRun(run);
                };
            }
        }
        if (resolvedType === 'vehicles') {
            setItem(resolvedType)
            setFields(jsonfile[weblanguage]['table'])
        } else if (resolvedType === 'complaints') {
            setItem(resolvedType)

        } else {
            navto('/table/vehicles')
        }

        return undefined;

    }, [abortRun, isActive, isCurrentRun, jsonfile, navto, readCookie, refreshVehicles, resolvedType, startRequestRun, vehWithComp.length, weblanguage])
    useEffect(() => {
        if (!isActive || resolvedType !== 'vehicles') return;

        setData(vehWithComp);
        if (vehWithComp.length !== 0) {
            const run = startRequestRun();
            const signal = run.controller.signal;
            const enrichVehicleData = async (vehicles) => {
                setIsLoading(true);
                const cookie = readCookie();
                try {
                    const enrichedVehicles = await enrichWithConcurrency(
                        vehicles,
                        ENRICHMENT_CONCURRENCY,
                        signal,
                        async (vehicle) => {
                            try {
                                const [liveData, dailyVehicleData] = await Promise.all([
                                    fetchAdditionalVehicleData(vehicle.chassis_number, cookie, { signal }),
                                    fetchDailyVehicleData(vehicle.chassis_number, cookie, { signal }),
                                ]);
                                throwIfAborted(signal);
                                const datedEffWithMileage = dailyVehicleData.reduce((dict, record) => {
                                    dict[record.date] = { efficiency: record.efficiency, dailyMileage: record.dailyMileage };
                                    return dict;
                                }, {});
                                return {
                                    ...vehicle,
                                    soc: liveData?.[0]?.soc ?? null,
                                    mileage: liveData?.[0]?.mileage_dm ?? null,
                                    timestamp: liveData?.[0]?.timestamp ?? null,
                                    dated_eff: datedEffWithMileage || {}
                                };
                            } catch (error) {
                                if (isRequestCanceled(error) || signal.aborted) {
                                    throw error;
                                }
                                return {
                                    ...vehicle,
                                    soc: null,
                                    mileage: null,
                                    timestamp: null,
                                    dated_eff: {}
                                };
                            }
                        }
                    );
                    if (isCurrentRun(run)) {
                        setData(enrichedVehicles);
                    }
                } catch (error) {
                    if (!isRequestCanceled(error) && !signal.aborted) {
                        // Keep the base vehicle list visible if enrichment fails.
                    }
                } finally {
                    if (isCurrentRun(run)) {
                        setIsLoading(false);
                    }
                }
            };
            enrichVehicleData(vehWithComp);
            return () => {
                abortRun(run);
            };
        }
        return undefined;
    }, [abortRun, isActive, isCurrentRun, resolvedType, vehWithComp, fetchAdditionalVehicleData, readCookie, startRequestRun]);

    useEffect(() => {
        if (!isActive) {
            abortCurrentRun();
        }
    }, [abortCurrentRun, isActive]);

    useEffect(() => {
        window.addEventListener(AUTH_EXPIRED_EVENT, abortCurrentRun);
        return () => {
            window.removeEventListener(AUTH_EXPIRED_EVENT, abortCurrentRun);
            abortCurrentRun();
        };
    }, [abortCurrentRun]);

    useEffect(() => {
        if (!isActive) return;

        if (resolvedType === 'vehicles') {
            setItem(resolvedType)
            setData(vehWithComp)
        } else if (resolvedType === 'complaints') {
            setItem(resolvedType)
        } else {
            navto('/table/vehicles')
            setItem('vehicles')
        }
    }, [isActive, navto, resolvedType, vehWithComp])
    useEffect(() => {
        setFields(jsonfile[weblanguage]['table'])
    }, [jsonfile, weblanguage])

    const getVehicleSearchStatusMessage = () => {
        if (searchStatus === 'searching') return tableFields.searching || 'Searching...';
        if (searchStatus === 'found') return tableFields.searchFound || 'Vehicle found.';
        if (searchStatus === 'no-exact-local') {
            return tableFields.searchNoExactSelection || 'No exact vehicle was selected. Showing matching vehicles only.';
        }
        if (searchStatus === 'not-found') return tableFields.searchNotFound || 'No vehicle found for this plate.';
        if (hasCurrentPlateSearchInput && !activeSearchedVehicle) {
            return displayedVehicles.length > 0
                ? tableFields.searchFiltered || 'Vehicles filtered.'
                : tableFields.searchNoLocalMatches || 'No vehicles match this plate.';
        }
        return '';
    };

    const vehicleSearchStatusMessage = getVehicleSearchStatusMessage();
    const emptyVehicleMessage = hasCurrentPlateSearchInput
        ? tableFields.searchNoLocalMatches || 'No vehicles match this plate.'
        : (fields ? fields['notfound'] : '');

    const ShellElement = isEmbedded ? 'section' : 'div';
    const shellClassName = isEmbedded ? `fleet-overview-section ${webtheme}-theme` : 'tables-page-shell';

    return (
        <ShellElement className={shellClassName} dir={weblanguage === 'ar' ? 'rtl' : 'ltr'}>
            {showTitle && (
                <div className="fleet-overview-heading">
                    <h2>{fleetOverviewCopy.title || 'Fleet Overview'}</h2>
                </div>
            )}
            {isLoading && hasLiveVehicleData && (
                <LoadingScreen
                    mode="inline"
                    title={liveRefreshingTitle}
                    message={liveRefreshingMessage}
                />
            )}
            <div className={`vehicle-search-shell ${webtheme}-theme`}>
                <VehicleSearchForm
                    onSearch={getSingleVehicle}
                    onClear={handleClearSearch}
                    plateLetters={plateLetters}
                    setPlateLetters={handlePlateLettersChange}
                    plateNumber={plateNumber}
                    setPlateNumber={handlePlateNumberChange}
                    isLangError={isLangError}
                    langErrorMessage={langErrorMessage}
                    isNumError={isNumError}
                    numErrorMessage={numErrorMessage}
                    isSearching={isVehicleSearchLoading}
                    statusMessage={vehicleSearchStatusMessage}
                    fields={tableFields}
                    isRtl={weblanguage === 'ar'}
                    theme={webtheme}
                    canClearSearch={hasActiveSearchState}
                />

                {activeSearchedVehicle && (
                    <div className={`vehicle-search-result ${webtheme}-theme`} role="status" aria-live="polite">
                        <div>
                            <span className="vehicle-search-result-kicker">{tableFields.filteredBy || 'Filtered vehicle'}</span>
                            <strong>{getVehicleDisplayName(activeSearchedVehicle)}</strong>
                            <span>{activeSearchedVehicle.chassis_number}</span>
                        </div>
                        <LicensePlate plateNumber={activeSearchedVehicle.license_plate} />
                        <button type="button" className="vehicle-search-result-clear" onClick={getAllVehicles}>
                            {tableFields.display || 'Display all vehicles'}
                        </button>
                    </div>
                )}
            </div>
            {/* Main container for vehicle cards */}
            <div className='vehicle-cards-container' aria-busy={isLoading}>
                {isLoading && !hasLiveVehicleData ? (
                    <LoadingScreen
                        mode="panel"
                        title={liveLoadingTitle}
                        message={liveLoadingMessage}
                    />
                ) : item === 'complaints' ? (
                    <></>
                ) : (
                    <>
                        {data ? (
                            <>
                                <div className={`not-found-message ${showNoVehiclesMessage ? 'd-block' : 'd-none'}`}>
                                    {emptyVehicleMessage}
                                    <button className='btn btn-orange fs-7 text-light' onClick={getAllVehicles}>
                                        {fields ? fields['display'] : ''}
                                    </button>
                                </div>

                                {displayedVehicles.length > 0 && (
                                    <div className={`vehicle-table-wrapper ${webtheme}-theme`}>
                                        <table className={`table vehicle-table`}>
                                            <thead>
                                                <tr>
                                                    <th>{fields ? fields['plate'] : 'Plate'}</th>
                                                    <th>{fields ? fields['vehicle'] : 'Vehicle'}</th>
                                                    <th>{fields ? fields['soc'] : 'SOC'}</th>
                                                    <th>{fields ? fields['mileage'] : 'Mileage'}</th>
                                                    <th>{fields ? fields['status'] : 'Status'}</th>
                                                    <th>{fields ? fields['actions'] : 'Action'}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {displayedVehicles.map((vehicle) => (
                                                    <VehicleTableRow
                                                        key={vehicle.chassis_number}
                                                        vehicle={vehicle}
                                                        webtheme={webtheme}
                                                        weblanguage={weblanguage}
                                                        navto={navto}
                                                        fields={fields}
                                                        language={weblanguage}
                                                    />
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                {showMore && (
                                    <div className='show-more-container'>
                                        <button className='btn btn-orange fs-7 text-light' onClick={() => { getAllVehicles(); setShowMore(false); }}>
                                            {fields ? fields['display'] : ''}
                                        </button>
                                    </div>
                                )}
                            </>
                        ) : (
                            <LoadingScreen
                                mode="panel"
                                title={liveLoadingTitle}
                                message={liveLoadingMessage}
                            />
                        )}
                    </>
                )}
            </div>
        </ShellElement>
    )
}
